/**
 * Complex Workflow Visualizer Component
 * 
 * Handles complex, cyclical business processes with multiple actors,
 * parallel tracks, loops, and external integrations.
 * 
 * Designed for real-world scenarios like Healthcare RCM, Supply Chain,
 * Manufacturing, and other domain-specific complex workflows.
 * 
 * *** POWERED BY GRAPHQL WORKFLOW TYPE CHAINING ***
 * - Real-time visualization of workflow dependencies and relationships
 * - Cross-workflow impact analysis when modifying complex processes
 * - AI-powered optimization suggestions with complete ecosystem awareness
 * - Live dependency mapping shows how changes cascade through the system
 */

import { gql } from '@apollo/client';
import { useQuery } from '@ferp/graphql-client';
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    Grid,
    Group,
    Modal,
    Paper,
    ScrollArea,
    SegmentedControl,
    Slider,
    Stack,
    Text,
    Timeline,
    Title,
    Tooltip
} from '@mantine/core';
import {
    IconActivity,
    IconBrandStackshare,
    IconClockPause,
    IconExternalLink,
    IconFileImport,
    IconHeartbeat,
    IconInfoCircle,
    IconLayersLinked,
    IconRefresh,
    IconUsers,
    IconZoomIn,
    IconZoomOut
} from '@tabler/icons-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DOMAIN_PROCESSES } from './DomainProcessLibrary';

// Default to Healthcare RCM Process
const HEALTHCARE_RCM_PROCESS = DOMAIN_PROCESSES.HEALTHCARE_RCM || {
    id: 'healthcare-rcm',
    name: 'Revenue Cycle Management',
    domain: 'Healthcare',
    stages: [
        {
            id: 'patient-acquisition',
            name: 'Patient Acquisition',
            type: 'entry',
            actors: ['Patient', 'Front Desk', 'System'],
            steps: [
                { id: 'registration', name: 'Patient Registration', actor: 'Front Desk' },
                { id: 'insurance-verify', name: 'Insurance Verification', actor: 'System' },
                { id: 'eligibility-check', name: 'Eligibility Check', actor: 'System' }
            ]
        },
        {
            id: 'pre-authorization',
            name: 'Pre-Authorization',
            type: 'process',
            actors: ['Clinical Staff', 'Insurance', 'System'],
            steps: [
                { id: 'medical-necessity', name: 'Medical Necessity Review', actor: 'Clinical Staff' },
                { id: 'auth-request', name: 'Submit Auth Request', actor: 'System' },
                { id: 'auth-response', name: 'Receive Authorization', actor: 'Insurance' }
            ]
        },
        {
            id: 'service-delivery',
            name: 'Service Delivery',
            type: 'process',
            actors: ['Provider', 'Nurse', 'Patient'],
            steps: [
                { id: 'schedule', name: 'Schedule Service', actor: 'Front Desk' },
                { id: 'staff-verify', name: 'Verify Staff Credentials', actor: 'System' },
                { id: 'render-service', name: 'Render Service', actor: 'Provider' },
                { id: 'document', name: 'Document Care', actor: 'Provider' }
            ],
            loops: true // This stage can repeat
        },
        {
            id: 'claims-submission',
            name: 'Claims & Billing',
            type: 'process',
            actors: ['Billing', 'System', 'Payer'],
            steps: [
                { id: 'coding', name: 'Medical Coding', actor: 'Billing' },
                { id: 'claim-generation', name: 'Generate 837 Claim', actor: 'System' },
                { id: 'x12-submission', name: 'Submit X12 File', actor: 'System', external: true },
                { id: 'claim-status', name: 'Check Claim Status', actor: 'System' }
            ]
        },
        {
            id: 'payment-posting',
            name: 'Payment & Remittance',
            type: 'process',
            actors: ['Payer', 'Billing', 'System'],
            steps: [
                { id: 'era-835', name: 'Receive 835 ERA', actor: 'System', external: true },
                { id: 'payment-post', name: 'Post Payment', actor: 'Billing' },
                { id: 'denial-management', name: 'Handle Denials', actor: 'Billing', conditional: true }
            ],
            loops: true // Can loop back to claims
        },
        {
            id: 'patient-collections',
            name: 'Patient Collections',
            type: 'process',
            actors: ['Patient', 'Collections', 'System'],
            steps: [
                { id: 'patient-statement', name: 'Generate Statement', actor: 'System' },
                { id: 'payment-collection', name: 'Collect Payment', actor: 'Collections' },
                { id: 'payment-plan', name: 'Setup Payment Plan', actor: 'Collections', conditional: true }
            ]
        },
        {
            id: 'lifecycle-management',
            name: 'Patient Lifecycle',
            type: 'meta',
            actors: ['Patient', 'System'],
            steps: [
                { id: 'ongoing-care', name: 'Ongoing Care Loop', loops: true },
                { id: 'discharge', name: 'Discharge/Transfer', conditional: true },
                { id: 'readmission', name: 'Readmission', loops: true, conditional: true }
            ]
        }
    ],
    connections: [
        { from: 'patient-acquisition', to: 'pre-authorization' },
        { from: 'pre-authorization', to: 'service-delivery' },
        { from: 'service-delivery', to: 'claims-submission' },
        { from: 'claims-submission', to: 'payment-posting' },
        { from: 'payment-posting', to: 'patient-collections' },
        { from: 'payment-posting', to: 'claims-submission', type: 'loop', condition: 'denial' },
        { from: 'service-delivery', to: 'service-delivery', type: 'loop', condition: 'ongoing' },
        { from: 'patient-collections', to: 'lifecycle-management' },
        { from: 'lifecycle-management', to: 'service-delivery', type: 'loop', condition: 'readmit' }
    ]
};

interface ComplexWorkflowVisualizerProps {
    processDefinition?: typeof HEALTHCARE_RCM_PROCESS;
    viewMode?: 'spiral' | 'swimlane' | 'flow' | 'timeline' | 'matrix';
    onStepClick?: (step: any) => void;
    onProcessSelect?: (processId: string) => void;
    navigateOnSelect?: boolean; // if true, navigate to workflows route on selection
    targetWorkflowType?: string; // defaults to 'WorkflowDefinition'
    navigateFn?: (url: string) => void; // optional navigation function from host app
}

export const ComplexWorkflowVisualizer: React.FC<ComplexWorkflowVisualizerProps> = ({
    processDefinition: initialProcess = HEALTHCARE_RCM_PROCESS,
    viewMode: initialViewMode = 'spiral',
    onStepClick,
    onProcessSelect,
    navigateOnSelect = true,
    targetWorkflowType = 'WorkflowDefinition',
    navigateFn
}) => {
    let navigate: ((url: string) => void) | null = null;
    try {
        // Prefer host-provided fn; otherwise attempt router navigate if available
        const hookNav = useNavigate as unknown as (() => (to: string) => void);
        if (!navigateFn && typeof hookNav === 'function') {
            try { navigate = hookNav(); } catch { navigate = null; }
        }
    } catch { /* no router in this bundle */ }
    const [viewMode, setViewMode] = useState(initialViewMode);
    const [selectedDomain, setSelectedDomain] = useState(initialProcess.domain);
    const [selectedProcessId, setSelectedProcessId] = useState(initialProcess.id);
    const [processDefinition, setProcessDefinition] = useState<typeof HEALTHCARE_RCM_PROCESS>(initialProcess as any);
    const [selectedStage, setSelectedStage] = useState<string | null>(null);
    const [zoom, setZoom] = useState(100);
    const [showActors, setShowActors] = useState(true);
    const [showLoops, setShowLoops] = useState(true);
    const [showExternal, setShowExternal] = useState(true);
    const [playbackPosition, setPlaybackPosition] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [showProcessSelector, setShowProcessSelector] = useState(false);

    const canvasRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<number | null>(null);

    // Calculate process metrics
    const totalSteps = processDefinition.stages.reduce((acc, stage) => acc + (stage.steps?.length || 0), 0);
    const loopingStages = processDefinition.stages.filter(s => s.loops).length;
    const externalIntegrations = processDefinition.stages.flatMap(s => (s.steps || []).filter(step => (step as any).external)).length;
    const uniqueActors = [...new Set(processDefinition.stages.flatMap(s => s.actors))].length;

    // Load available complex processes from GraphQL (no static fallback)
    const LIST_COMPLEX_PROCESSES = gql`
        query ListComplexProcesses($tenant: String!) {
            listDrafts(input: { tenant: $tenant, kind: "ComplexProcess" }) {
                key
            }
        }
    `;
    const { data: complexList } = useQuery<Record<string, any>>(LIST_COMPLEX_PROCESSES, { variables: { tenant: 'test' } });

    const availableLibraryProcesses = useMemo(() => {
        try {
            const keys = (complexList?.listDrafts || []).map((x: any) => String(x.key));
            const ids = keys
                .filter((k: string) => k.startsWith('proc:'))
                .map((k: string) => k.substring('proc:'.length));
            const all = Object.values(DOMAIN_PROCESSES) as any[];
            return all.filter(p => ids.includes(p.id));
        } catch {
            return [] as any[];
        }
    }, [complexList?.listDrafts]);

    const allDomains = useMemo(() => {
        const set = new Set<string>();
        for (const p of availableLibraryProcesses) set.add(p.domain);
        return Array.from(set);
    }, [availableLibraryProcesses]);

    useEffect(() => {
        // If current selection is not available, switch to first available
        if (availableLibraryProcesses.length > 0) {
            const has = availableLibraryProcesses.some(p => p.id === selectedProcessId);
            if (!has) {
                const first = availableLibraryProcesses[0];
                setSelectedProcessId(first.id);
                setSelectedDomain(first.domain);
                setProcessDefinition(first as any);
            }
        }
    }, [availableLibraryProcesses]);

    // Handle process selection
    const handleProcessChange = (newProcessId: string) => {
        const newProcess = (Object.values(DOMAIN_PROCESSES) as any[]).find(p => p.id === newProcessId);
        if (newProcess) {
            setProcessDefinition(newProcess as any);
            setSelectedProcessId(newProcessId);
            setSelectedDomain(newProcess.domain);
            setSelectedStage(null);
            setPlaybackPosition(0);

            // Notify parent if provided
            try { onProcessSelect?.(newProcessId); } catch {}

            // Emit a loose-coupled event for any shell listeners
            try { window.dispatchEvent(new CustomEvent('workflow:processSelected', { detail: { processId: newProcessId } })); } catch {}

            // Optionally navigate to standard workflows view so the app reflects selection
            if (navigateOnSelect) {
                try {
                    const type = String(targetWorkflowType || 'WorkflowDefinition');
                    const url = `/workflows/${encodeURIComponent(type)}?process=${encodeURIComponent(newProcessId)}`;
                    if (typeof navigateFn === 'function') {
                        navigateFn(url);
                    } else if (typeof navigate === 'function') {
                        navigate(url);
                    } else if (typeof window !== 'undefined' && window.history && window.history.pushState) {
                        // fallback: push URL and emit popstate-like event
                        window.history.pushState({}, '', url);
                        try { window.dispatchEvent(new PopStateEvent('popstate')); } catch {}
                    }
                } catch {}
            }
        }
    };

    // Get available processes for selected domain (from dynamic list)
    const domainProcesses = useMemo(() => {
        return availableLibraryProcesses.filter(p => p.domain === selectedDomain);
    }, [availableLibraryProcesses, selectedDomain]);

    // Render Spiral View (for cyclical processes)
    const renderSpiralView = () => {
        const centerX = 300;
        const centerY = 300;
        const spiralGrowth = 40;
        let angle = 0;
        let radius = 50;

        return (
            <svg width="600" height="600" viewBox="0 0 600 600" style={{ width: '100%' }}>
                {/* Draw spiral path */}
                <path
                    d={`M ${centerX} ${centerY} ${processDefinition.stages.map((stage, i) => {
                        angle += Math.PI / 3;
                        radius += spiralGrowth;
                        const x = centerX + radius * Math.cos(angle);
                        const y = centerY + radius * Math.sin(angle);
                        return `L ${x} ${y}`;
                    }).join(' ')}`}
                    fill="none"
                    stroke="url(#spiralGradient)"
                    strokeWidth="3"
                    strokeDasharray={showLoops ? "5,5" : "none"}
                />

                {/* Gradient definition */}
                <defs>
                    <linearGradient id="spiralGradient">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="50%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#f59e0b" />
                    </linearGradient>
                </defs>

                {/* Place stages on spiral */}
                {(() => {
                    angle = 0;
                    radius = 50;
                    return processDefinition.stages.map((stage, i) => {
                        angle += Math.PI / 3;
                        radius += spiralGrowth;
                        const x = centerX + radius * Math.cos(angle);
                        const y = centerY + radius * Math.sin(angle);

                        return (
                            <g key={stage.id}>
                                {/* Stage circle */}
                                <circle
                                    cx={x}
                                    cy={y}
                                    r="30"
                                    fill={selectedStage === stage.id ? '#3b82f6' : 'white'}
                                    stroke="#3b82f6"
                                    strokeWidth="2"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedStage(stage.id)}
                                />

                                {/* Stage number */}
                                <text
                                    x={x}
                                    y={y + 5}
                                    textAnchor="middle"
                                    fontSize="16"
                                    fontWeight="bold"
                                    fill={selectedStage === stage.id ? 'white' : '#3b82f6'}
                                >
                                    {i + 1}
                                </text>

                                {/* Stage name */}
                                <text
                                    x={x}
                                    y={y + 50}
                                    textAnchor="middle"
                                    fontSize="12"
                                    fill="#374151"
                                >
                                    {stage.name}
                                </text>

                                {/* Loop indicator */}
                                {stage.loops && showLoops && (
                                    <circle
                                        cx={x + 25}
                                        cy={y - 25}
                                        r="8"
                                        fill="#f59e0b"
                                    />
                                )}
                            </g>
                        );
                    });
                })()}

                {/* Loop connections */}
                {showLoops && processDefinition.connections
                    .filter(conn => conn.type === 'loop')
                    .map((conn, i) => (
                        <path
                            key={`loop-${i}`}
                            d={`M 200 200 Q 400 150, 300 300`}
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth="2"
                            strokeDasharray="5,5"
                            markerEnd="url(#arrowhead)"
                        />
                    ))
                }

                {/* Arrow marker */}
                <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="#3b82f6" />
                    </marker>
                </defs>
            </svg>
        );
    };

    // Render Swimlane View (for actor-based processes)
    const renderSwimlaneView = () => {
        const actors = [...new Set(processDefinition.stages.flatMap(s => s.actors))];
        const laneHeight = 100;

        return (
            <Box style={{ position: 'relative', minHeight: actors.length * laneHeight }}>
                {actors.map((actor, laneIndex) => (
                    <Paper
                        key={actor}
                        withBorder
                        p="md"
                        mb="xs"
                        style={{
                            background: laneIndex % 2 === 0 ? '#f9fafb' : 'white',
                            minHeight: laneHeight
                        }}
                    >
                        <Group justify="space-between">
                            <Group>
                                <IconUsers size={20} />
                                <Text fw={500}>{actor}</Text>
                            </Group>
                            <ScrollArea style={{ flex: 1 }} scrollbarSize={6}>
                                <Group gap="xs">
                                    {processDefinition.stages.flatMap(stage =>
                                        stage.steps
                                            .filter(step => step.actor === actor)
                                            .map(step => (
                                                <Badge
                                                    key={step.id}
                                                    variant="filled"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => onStepClick?.(step)}
                                                >
                                                    {step.name}
                                                    {step.external && ' 🔗'}
                                                </Badge>
                                            ))
                                    )}
                                </Group>
                            </ScrollArea>
                        </Group>
                    </Paper>
                ))}
            </Box>
        );
    };

    // Render Timeline View
    const renderTimelineView = () => (
        <Timeline active={playbackPosition} lineWidth={2}>
            {processDefinition.stages.map((stage, index) => (
                <Timeline.Item
                    key={stage.id}
                    bullet={
                        stage.type === 'entry' ? <IconFileImport size={12} /> :
                            stage.type === 'meta' ? <IconLayersLinked size={12} /> :
                                stage.loops ? <IconRefresh size={12} /> :
                                    <IconActivity size={12} />
                    }
                    title={stage.name}
                >
                    <Text c="dimmed" size="sm">
                        {stage.actors.join(', ')}
                    </Text>
                    <Stack gap={4} mt="xs">
                        {(stage.steps || []).map(step => (
                            <Group key={step.id} gap={4}>
                                <Checkbox
                                    size="xs"
                                    checked={index <= playbackPosition}
                                    readOnly
                                />
                                <Text size="xs">
                                    {step.name}
                                    {(step as any).external && ' 🔗'}
                                    {(step as any).conditional && ' ⚡'}
                                </Text>
                            </Group>
                        ))}
                    </Stack>
                    {stage.loops && (
                        <Badge color="orange" size="xs" mt="xs">
                            Repeatable Stage
                        </Badge>
                    )}
                </Timeline.Item>
            ))}
        </Timeline>
    );

    // Render Matrix View (for complex dependencies)
    const renderMatrixView = () => {
        const stages = processDefinition.stages;

        return (
            <Box>
                <ScrollArea>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ padding: 8, border: '1px solid #e5e7eb' }}>From / To</th>
                                {stages.map(stage => (
                                    <th key={stage.id} style={{ padding: 8, border: '1px solid #e5e7eb' }}>
                                        <Text size="xs" style={{ writingMode: 'vertical-rl' }}>
                                            {stage.name}
                                        </Text>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {stages.map(fromStage => (
                                <tr key={fromStage.id}>
                                    <td style={{ padding: 8, border: '1px solid #e5e7eb', fontWeight: 500 }}>
                                        {fromStage.name}
                                    </td>
                                    {stages.map(toStage => {
                                        const connection = processDefinition.connections.find(
                                            c => c.from === fromStage.id && c.to === toStage.id
                                        );
                                        return (
                                            <td
                                                key={toStage.id}
                                                style={{
                                                    padding: 8,
                                                    border: '1px solid #e5e7eb',
                                                    textAlign: 'center',
                                                    background: connection ?
                                                        (connection.type === 'loop' ? '#fef3c7' : '#dbeafe') :
                                                        'white'
                                                }}
                                            >
                                                {connection && (
                                                    connection.type === 'loop' ? '🔄' : '→'
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ScrollArea>
            </Box>
        );
    };

    // Render Flow View (traditional flowchart)
    const renderFlowView = () => (
        <Box ref={canvasRef} style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}>
            <svg width="800" height="600" viewBox="0 0 800 600">
                {/* Render stages as nodes */}
                {processDefinition.stages.map((stage, i) => {
                    const x = 100 + (i % 3) * 250;
                    const y = 50 + Math.floor(i / 3) * 150;

                    return (
                        <g key={stage.id}>
                            {/* Stage box */}
                            <rect
                                x={x}
                                y={y}
                                width="200"
                                height="80"
                                rx="8"
                                fill={
                                    stage.type === 'entry' ? '#dbeafe' :
                                        stage.type === 'meta' ? '#fef3c7' :
                                            stage.loops ? '#dcfce7' : '#f3f4f6'
                                }
                                stroke="#6b7280"
                                strokeWidth="2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedStage(stage.id)}
                            />

                            {/* Stage text */}
                            <text x={x + 100} y={y + 30} textAnchor="middle" fontSize="14" fontWeight="500">
                                {stage.name}
                            </text>

                            {/* Actor count */}
                            <text x={x + 100} y={y + 50} textAnchor="middle" fontSize="12" fill="#6b7280">
                                {stage.actors.length} actors, {stage.steps.length} steps
                            </text>

                            {/* External indicator */}
                            {stage.steps.some(s => s.external) && showExternal && (
                                <circle cx={x + 180} cy={y + 20} r="8" fill="#3b82f6" />
                            )}

                            {/* Loop indicator */}
                            {stage.loops && showLoops && (
                                <path
                                    d={`M ${x + 160} ${y + 60} Q ${x + 180} ${y + 40}, ${x + 160} ${y + 40}`}
                                    fill="none"
                                    stroke="#f59e0b"
                                    strokeWidth="2"
                                    markerEnd="url(#loopArrow)"
                                />
                            )}
                        </g>
                    );
                })}

                {/* Render connections */}
                {processDefinition.connections.map((conn, i) => {
                    const fromIndex = processDefinition.stages.findIndex(s => s.id === conn.from);
                    const toIndex = processDefinition.stages.findIndex(s => s.id === conn.to);

                    const x1 = 100 + (fromIndex % 3) * 250 + 200;
                    const y1 = 50 + Math.floor(fromIndex / 3) * 150 + 40;
                    const x2 = 100 + (toIndex % 3) * 250;
                    const y2 = 50 + Math.floor(toIndex / 3) * 150 + 40;

                    return (
                        <line
                            key={i}
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke={conn.type === 'loop' ? '#f59e0b' : '#6b7280'}
                            strokeWidth="2"
                            strokeDasharray={conn.type === 'loop' ? '5,5' : 'none'}
                            markerEnd="url(#arrow)"
                        />
                    );
                })}

                {/* Arrow markers */}
                <defs>
                    <marker id="arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="#6b7280" />
                    </marker>
                    <marker id="loopArrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="#f59e0b" />
                    </marker>
                </defs>
            </svg>
        </Box>
    );

    // Animation for timeline playback
    useEffect(() => {
        if (isPlaying) {
            animationRef.current = window.setInterval(() => {
                setPlaybackPosition(prev => {
                    if (prev >= processDefinition.stages.length - 1) {
                        setIsPlaying(false);
                        return 0;
                    }
                    return prev + 1;
                });
            }, 2000);
        } else if (animationRef.current) {
            clearInterval(animationRef.current);
        }

        return () => {
            if (animationRef.current) clearInterval(animationRef.current);
        };
    }, [isPlaying, processDefinition.stages.length]);

    return (
        <Box>
            <Paper shadow="sm" p="lg">
                {/* Header */}
                <Group justify="space-between" mb="xl">
                    <div>
                        <Group>
                            <IconHeartbeat size={28} color="var(--mantine-color-blue-6)" />
                            <div>
                                <Title order={2}>{processDefinition.name}</Title>
                                <Text c="dimmed">{processDefinition.domain} - Complex Process Visualization</Text>
                            </div>
                        </Group>
                    </div>

                    <Group>
                        {/* Process Selector Button */}
                        <Button
                            variant="light"
                            leftSection={<IconBrandStackshare size={18} />}
                            onClick={() => setShowProcessSelector(true)}
                        >
                            Change Process
                        </Button>

                        {/* View Mode Selector */}
                        <SegmentedControl
                            value={viewMode}
                            onChange={(v: string) => setViewMode(v as any)}
                            data={[
                                { label: '🌀 Spiral', value: 'spiral' },
                                { label: '🏊 Swimlane', value: 'swimlane' },
                                { label: '🔀 Flow', value: 'flow' },
                                { label: '📅 Timeline', value: 'timeline' },
                                { label: '📊 Matrix', value: 'matrix' }
                            ]}
                        />

                        {/* Help Button */}
                        <Tooltip label="Understanding complex workflows">
                            <ActionIcon variant="default" onClick={() => setShowHelp(true)}>
                                <IconInfoCircle size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Group>

                {/* Process Metrics */}
                <Grid mb="xl">
                    <Grid.Col span={3}>
                        <Paper withBorder p="xs">
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">Total Steps</Text>
                                <Badge size="lg" variant="filled">{totalSteps}</Badge>
                            </Group>
                        </Paper>
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <Paper withBorder p="xs">
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">Unique Actors</Text>
                                <Badge size="lg" variant="filled" color="teal">{uniqueActors}</Badge>
                            </Group>
                        </Paper>
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <Paper withBorder p="xs">
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">Looping Stages</Text>
                                <Badge size="lg" variant="filled" color="orange">{loopingStages}</Badge>
                            </Group>
                        </Paper>
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <Paper withBorder p="xs">
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">External Systems</Text>
                                <Badge size="lg" variant="filled" color="blue">{externalIntegrations}</Badge>
                            </Group>
                        </Paper>
                    </Grid.Col>
                </Grid>

                {/* Controls */}
                {(viewMode === 'flow' || viewMode === 'spiral') && (
                    <Paper withBorder p="sm" mb="md">
                        <Group>
                            <Group gap="xs">
                                <ActionIcon onClick={() => setZoom(Math.max(50, zoom - 10))}>
                                    <IconZoomOut size={18} />
                                </ActionIcon>
                                <Text size="sm">{zoom}%</Text>
                                <ActionIcon onClick={() => setZoom(Math.min(150, zoom + 10))}>
                                    <IconZoomIn size={18} />
                                </ActionIcon>
                            </Group>

                            <Divider orientation={'vertical' as const} />

                            <Checkbox
                                label="Show Actors"
                                checked={showActors}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowActors(e.currentTarget.checked)}
                            />

                            <Checkbox
                                label="Show Loops"
                                checked={showLoops}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowLoops(e.currentTarget.checked)}
                            />

                            <Checkbox
                                label="External Systems"
                                checked={showExternal}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowExternal(e.currentTarget.checked)}
                            />
                        </Group>
                    </Paper>
                )}

                {viewMode === 'timeline' && (
                    <Paper withBorder p="sm" mb="md">
                        <Group>
                            <Button
                                leftSection={isPlaying ? <IconClockPause size={16} /> : <IconActivity size={16} />}
                                onClick={() => setIsPlaying(!isPlaying)}
                            >
                                {isPlaying ? 'Pause' : 'Play'} Timeline
                            </Button>
                            <Slider
                                value={playbackPosition}
                                onChange={setPlaybackPosition}
                                max={processDefinition.stages.length - 1}
                                marks={processDefinition.stages.map((s, i) => ({ value: i }))}
                                style={{ flex: 1 }}
                            />
                        </Group>
                    </Paper>
                )}

                {/* Main Visualization */}
                <ScrollArea h={600}>
                    <Box style={{ minHeight: 500 }}>
                        {viewMode === 'spiral' && renderSpiralView()}
                        {viewMode === 'swimlane' && renderSwimlaneView()}
                        {viewMode === 'flow' && renderFlowView()}
                        {viewMode === 'timeline' && renderTimelineView()}
                        {viewMode === 'matrix' && renderMatrixView()}
                    </Box>
                </ScrollArea>

                {/* Selected Stage Details */}
                {selectedStage && (
                    <Paper withBorder p="md" mt="xl">
                        <Title order={4} mb="sm">
                            {processDefinition.stages.find(s => s.id === selectedStage)?.name} Details
                        </Title>
                        <Grid>
                            <Grid.Col span={4}>
                                <Text size="sm" fw={500}>Actors Involved:</Text>
                                <Group gap="xs" mt="xs">
                                    {processDefinition.stages
                                        .find(s => s.id === selectedStage)
                                        ?.actors.map(actor => (
                                            <Badge key={actor} variant="light">
                                                {actor}
                                            </Badge>
                                        ))
                                    }
                                </Group>
                            </Grid.Col>
                            <Grid.Col span={8}>
                                <Text size="sm" fw={500}>Process Steps:</Text>
                                <Stack gap="xs" mt="xs">
                                    {processDefinition.stages
                                        .find(s => s.id === selectedStage)
                                        ?.steps.map(step => (
                                            <Group key={step.id} gap="xs">
                                                <Badge size="sm" color="blue">
                                                    {step.name}
                                                </Badge>
                                                {step.external && <IconExternalLink size={14} />}
                                                {(step as any).conditional && <Text size="xs" c="dimmed">Conditional</Text>}
                                            </Group>
                                        ))
                                    }
                                </Stack>
                            </Grid.Col>
                        </Grid>
                    </Paper>
                )}
            </Paper>

            {/* Help Modal */}
            <Modal
                opened={showHelp}
                onClose={() => setShowHelp(false)}
                title="Complex Workflow Visualization"
                size="lg"
            >
                <Stack>
                    <div>
                        <Title order={4} mb="xs">Understanding Complex Processes</Title>
                        <Text size="sm">
                            Complex business processes like Healthcare RCM require specialized
                            visualization techniques to understand their cyclical nature, parallel
                            tracks, and multiple dependencies.
                        </Text>
                    </div>

                    <div>
                        <Title order={4} mb="xs">Visualization Modes</Title>
                        <Stack gap="xs">
                            <Paper withBorder p="sm">
                                <Group>
                                    <Text size="lg">🌀</Text>
                                    <div>
                                        <Text fw={500}>Spiral View</Text>
                                        <Text size="sm" c="dimmed">
                                            Shows the cyclical nature of processes that repeat over time
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>

                            <Paper withBorder p="sm">
                                <Group>
                                    <Text size="lg">🏊</Text>
                                    <div>
                                        <Text fw={500}>Swimlane View</Text>
                                        <Text size="sm" c="dimmed">
                                            Organizes steps by actor/role responsibility
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>

                            <Paper withBorder p="sm">
                                <Group>
                                    <Text size="lg">🔀</Text>
                                    <div>
                                        <Text fw={500}>Flow View</Text>
                                        <Text size="sm" c="dimmed">
                                            Traditional flowchart with decision points and loops
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>

                            <Paper withBorder p="sm">
                                <Group>
                                    <Text size="lg">📅</Text>
                                    <div>
                                        <Text fw={500}>Timeline View</Text>
                                        <Text size="sm" c="dimmed">
                                            Sequential progression with playback capability
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>

                            <Paper withBorder p="sm">
                                <Group>
                                    <Text size="lg">📊</Text>
                                    <div>
                                        <Text fw={500}>Matrix View</Text>
                                        <Text size="sm" c="dimmed">
                                            Shows all possible transitions and dependencies
                                        </Text>
                                    </div>
                                </Group>
                            </Paper>
                        </Stack>
                    </div>

                    <div>
                        <Title order={4} mb="xs">Key Concepts</Title>
                        <Stack gap={4}>
                            <Group gap="xs">
                                <Badge color="orange">Loops</Badge>
                                <Text size="sm">Processes that repeat (e.g., ongoing care)</Text>
                            </Group>
                            <Group gap="xs">
                                <Badge color="blue">External</Badge>
                                <Text size="sm">Integration with outside systems (e.g., X12)</Text>
                            </Group>
                            <Group gap="xs">
                                <Badge color="green">Conditional</Badge>
                                <Text size="sm">Steps that occur based on conditions</Text>
                            </Group>
                        </Stack>
                    </div>
                </Stack>
            </Modal>

            {/* Process Selector Modal */}
            <Modal
                opened={showProcessSelector}
                onClose={() => setShowProcessSelector(false)}
                title="Select a Process to Visualize"
                size="xl"
            >
                <Stack>
                    <Text size="sm" c="dimmed">
                        Choose from pre-configured complex workflows across different industries
                    </Text>

                    <SegmentedControl
                        value={selectedDomain}
                        onChange={setSelectedDomain}
                        data={allDomains}
                        fullWidth
                    />

                    <Grid>
                        {domainProcesses.map(process => (
                            <Grid.Col key={process.id} span={6}>
                                <Card
                                    withBorder
                                    shadow={selectedProcessId === process.id ? 'lg' : 'sm'}
                                    style={{
                                        cursor: 'pointer',
                                        borderColor: selectedProcessId === process.id ?
                                            'var(--mantine-color-blue-6)' : undefined
                                    }}
                                    onClick={() => {
                                        handleProcessChange(process.id);
                                        setShowProcessSelector(false);
                                    }}
                                >
                                    <Group justify="space-between" mb="xs">
                                        <Title order={5}>{process.name}</Title>
                                        {selectedProcessId === process.id && (
                                            <Badge color="blue">Current</Badge>
                                        )}
                                    </Group>
                                    <Text size="sm" c="dimmed" mb="xs">
                                        {process.description || 'Complex workflow process'}
                                    </Text>
                                    <Group gap="xs">
                                        <Badge variant="light" size="sm">
                                            {process.complexity || 'medium'} complexity
                                        </Badge>
                                        <Badge variant="light" size="sm">
                                            {process.avgDuration || 'varies'}
                                        </Badge>
                                        <Badge variant="light" size="sm">
                                            {process.stages.length} stages
                                        </Badge>
                                    </Group>
                                </Card>
                            </Grid.Col>
                        ))}
                    </Grid>
                </Stack>
            </Modal>
        </Box>
    );
};

export default ComplexWorkflowVisualizer;
